import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, BarChart3, CalendarClock, CheckCircle2, Copy, Download, Eye, FileSpreadsheet, Loader2, Pencil, Play, Plus, Search, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin as checkIsSuperAdmin } from "../../lib/features";
import HelpTooltip from "../shared/HelpTooltip";

const STATUS_LABELS = {
  draft: "Rascunho",
  ready_to_send: "Pronta para envio",
  queued: "Na fila",
  running: "Enviando",
  completed: "Finalizada",
  completed_with_errors: "Finalizada com erros",
  failed: "Falhou",
  scheduled: "Agendada",
  sending: "Enviando",
  finished: "Finalizada",
  paused: "Pausada",
  cancelled: "Cancelada",
};

const STATUS_STYLES = {
  draft: "border-slate-700 bg-slate-800 text-slate-300",
  ready_to_send: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  queued: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  running: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  completed: "border-[#25D366]/30 bg-[#25D366]/10 text-[#25D366]",
  completed_with_errors: "border-orange-500/30 bg-orange-500/10 text-orange-300",
  failed: "border-red-500/30 bg-red-500/10 text-red-300",
  scheduled: "border-blue-500/30 bg-blue-500/10 text-blue-300",
  sending: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  finished: "border-[#25D366]/30 bg-[#25D366]/10 text-[#25D366]",
  paused: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  cancelled: "border-red-500/30 bg-red-500/10 text-red-300",
};

const EMPTY_DRAFT = {
  step: 1,
  name: "",
  channel_id: "",
  contacts: [],
  contact_source: "Nenhuma fonte selecionada",
  use_crm_contacts: false,
  tags: [],
  pipeline_id: "",
  column_id: "",
  message: "",
  template_name: "",
  template_language: "pt_BR",
  template_variables: { "1": "{{nome}}" },
  attachments: [],
  scheduled_at: "",
  scheduled_at_local: "",
};

function statusBadge(status) {
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>{STATUS_LABELS[status] || status}</span>;
}

function formatCampaignDate(value) {
  if (!value) return "-";
  const raw = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}T/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/.test(raw) ? `${raw}Z` : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

// Formata um Date pro formato aceito pelo <input type="datetime-local">
// (YYYY-MM-DDTHH:mm, em horario local do navegador).
function toLocalDatetimeInputValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatPhone(value) {
  const digits = onlyDigits(value);
  if (!digits) return "Telefone ausente";
  if (digits.startsWith("55") && digits.length >= 12) {
    const ddd = digits.slice(2, 4);
    const local = digits.slice(4);
    if (local.length === 9) return `+55 ${ddd} ${local.slice(0, 5)}-${local.slice(5)}`;
    if (local.length === 8) return `+55 ${ddd} ${local.slice(0, 4)}-${local.slice(4)}`;
  }
  return `+${digits}`;
}

function contactStatus(contact) {
  const digits = onlyDigits(contact?.phone);
  if (!digits) return { label: "Inválido", tone: "red", reason: "Telefone ausente" };
  if (digits.length < 10 || digits.length > 13) return { label: "Atenção", tone: "amber", reason: "Verificar telefone" };
  return { label: "Válido", tone: "green", reason: "Pronto para campanha" };
}

function renderMessageWithContactVariables(message, contact) {
  const original = String(message || "");
  if (!original) return "Mensagem não informada";
  if (!contact) return original;
  const values = {
    nome: contact.name || "",
    name: contact.name || "",
    telefone: contact.phone || "",
    phone: contact.phone || "",
    email: contact.email || "",
    empresa: contact.company || "",
    company: contact.company || "",
  };
  return original.replace(/\{\{\s*(nome|name|telefone|phone|email|empresa|company)\s*\}\}/gi, (_, key) => values[key.toLowerCase()] || "");
}
function contactStats(contacts = []) {
  const safeContacts = Array.isArray(contacts) ? contacts : [];
  return safeContacts.reduce((acc, contact) => {
    const status = contactStatus(contact);
    if (status.label === "Válido") acc.valid += 1;
    else if (status.label === "Atenção") acc.warning += 1;
    else acc.invalid += 1;
    acc.total += 1;
    return acc;
  }, { total: 0, valid: 0, warning: 0, invalid: 0 });
}

function downloadTemplateCsv() {
  const csv = [
    "nome,telefone,email,empresa,tag,campo_1,campo_2",
    "João Silva,5516999999999,joao@email.com,Empresa Exemplo,cliente_quente,valor opcional,valor opcional",
  ].join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modelo_importacao_contatos_cliver.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function normalizeType(value) {
  const raw = String(value || "").toLowerCase();
  if (raw.includes("meta") || raw === "whatsapp_meta") return "META";
  return "EVOLUTION";
}

function normalizeStatus(value, canSend) {
  const raw = String(value || "").toLowerCase();
  if (canSend || ["connected", "conectado", "open", "active"].includes(raw)) return "connected";
  if (["pending", "pendente", "qr", "qr_ready", "connecting"].includes(raw)) return "pending";
  return "disconnected";
}

function normalizeChannels(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.channels || [];
  return rows.map((channel) => {
    const type = normalizeType(channel.type || channel.provider);
    const id = channel.id || channel.channelId || channel.channel_id || `${type.toLowerCase()}:${channel.accountId || channel.instance_id || channel.instanceId || channel.name}`;
    const name = channel.displayName || channel.channelName || channel.name || (type === "META" ? "WhatsApp Oficial" : "WhatsApp Evolution");
    const canSend = Boolean(channel.canSend);
    return {
      ...channel,
      id,
      channel_id: channel.channel_id || channel.channelId || id,
      type,
      provider: type === "META" ? "meta" : "evolution",
      displayName: name,
      phone: channel.phone || channel.formattedPhone || channel.number || channel.rawPhone || "",
      status: normalizeStatus(channel.status || channel.rawStatus, canSend),
      canSend,
    };
  });
}

function parseCsv(text) {
  const rows = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!rows.length) return [];
  const headers = rows[0].split(/[;,]/).map((h) => h.trim().toLowerCase());
  const hasHeader = headers.some((h) => ["phone", "telefone", "whatsapp", "nome", "name"].includes(h));
  const dataRows = hasHeader ? rows.slice(1) : rows;
  return dataRows.map((row) => {
    const cols = row.split(/[;,]/).map((col) => col.trim());
    const get = (...names) => {
      if (!hasHeader) return "";
      for (const name of names) {
        const index = headers.indexOf(name);
        if (index >= 0) return cols[index] || "";
      }
      return "";
    };
    return {
      name: get("nome", "name") || cols[0] || "",
      phone: get("phone", "telefone", "whatsapp") || cols[1] || cols[0] || "",
      email: get("email", "e-mail"),
      company: get("empresa", "company"),
    };
  }).filter((item) => item.phone);
}

function channelTypeLabel(channel) {
  return channel?.type === "META" ? "Oficial Meta" : "Não oficial Evolution";
}

function channelProviderLabel(channel) {
  return channel?.type === "META" ? "Meta Cloud API" : "Evolution API";
}

function statusLabel(status) {
  if (status === "connected") return "Conectado";
  if (status === "pending") return "Pendente";
  return "Desconectado";
}

function getDraftErrors(draft, selectedChannel) {
  const errors = [];
  const stats = contactStats(draft?.contacts || []);
  if (!draft?.channel_id || !selectedChannel) errors.push("Selecione um canal WhatsApp.");
  if (selectedChannel && (selectedChannel.status !== "connected" || !selectedChannel.canSend)) errors.push("O canal selecionado precisa estar conectado.");
  if (!draft?.name?.trim()) errors.push("Informe o nome da campanha.");
  if (stats.valid <= 0) errors.push("Selecione ou importe pelo menos 1 contato válido.");
  if (selectedChannel?.type === "META" && !draft?.template_name) errors.push("Selecione um template oficial aprovado.");
  if (selectedChannel?.type === "EVOLUTION" && !draft?.message?.trim()) errors.push("Informe a mensagem livre para Evolution.");
  return errors;
}

// Validação separada do agendamento - não entra em getDraftErrors() de
// propósito, pra não afetar "Salvar rascunho" nem "Iniciar campanha".
function getScheduleErrors(draft) {
  const errors = [];
  if (!draft?.scheduled_at) {
    errors.push("Escolha uma data e hora para o agendamento.");
  } else {
    const scheduledTime = new Date(draft.scheduled_at).getTime();
    if (Number.isNaN(scheduledTime) || scheduledTime <= Date.now()) {
      errors.push("A data/hora do agendamento precisa ser no futuro.");
    }
  }
  return errors;
}

function canContinueStep(step, draft, selectedChannel) {
  if (step === 1) return Boolean(selectedChannel && selectedChannel.status === "connected" && selectedChannel.canSend);
  if (step === 2) return Boolean(draft.name?.trim());
  if (step === 3) return contactStats(draft.contacts || []).valid > 0;
  if (step === 4) return getDraftErrors(draft, selectedChannel).length === 0;
  return true;
}

function stepHint(step, draft, selectedChannel) {
  if (step === 1) return selectedChannel ? "Este canal precisa estar conectado para avançar." : "Selecione um canal WhatsApp conectado.";
  if (step === 2) return "Informe um nome para identificar a campanha.";
  if (step === 3) return "Importe contatos ou busque contatos do CRM.";
  if (step === 4) return getDraftErrors(draft, selectedChannel)[0] || "Revise os dados antes de iniciar.";
  return "";
}

export default function MassCampaignSettings() {
  const { user } = useAuth();
  // Distinção técnica (Meta Cloud API vs Evolution API, badges OFICIAL/NÃO
  // OFICIAL) só aparece pra super_admin -- pro resto dos usuários (incluindo
  // owner do tenant) a tela mostra só nome do canal + status de conexão.
  // Reaproveita o helper já usado em Sidebar.jsx (lib/features.js) em vez de
  // inventar um novo campo/local pra essa checagem.
  const isSuperAdmin = checkIsSuperAdmin(user);
  const [campaigns, setCampaigns] = useState([]);
  const [channels, setChannels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ tags: [], pipelines: [], columns: [] });
  const [draft, setDraft] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queueCampaign, setQueueCampaign] = useState(null);

  const selectedChannel = useMemo(() => channels.find((item) => item.id === draft?.channel_id), [channels, draft?.channel_id]);
  const isMeta = selectedChannel?.type === "META";
  const isEvolution = selectedChannel?.type === "EVOLUTION";
  const approvedTemplates = templates.filter((item) => item.status === "APPROVED");

  const load = async () => {
    const [campaignRes, channelRes, templateRes, filterRes] = await Promise.all([
      api.get("/mass-campaigns"),
      api.get("/mass-campaigns/channels"),
      api.get("/mass-campaigns/templates"),
      api.get("/mass-campaigns/filters"),
    ]);
    setCampaigns(campaignRes.data || []);
    setChannels(normalizeChannels(channelRes.data));
    setTemplates(templateRes.data || []);
    setFilters(filterRes.data || { tags: [], pipelines: [], columns: [] });
  };

  useEffect(() => { load().catch(() => toast.error("Erro ao carregar Envio em Massa")); }, []);

  const openNew = () => setDraft({ ...EMPTY_DRAFT, contacts: [], template_variables: { "1": "{{nome}}" } });
  const patchDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const importFile = async (file) => {
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".xlsx")) {
      toast.info("XLSX ainda não possui parser ativo nesta etapa. Use CSV por enquanto.");
      return;
    }
    const text = await file.text();
    const contacts = parseCsv(text);
    patchDraft({ contacts, contact_source: `Arquivo CSV: ${file.name}` });
    toast.success(`${contacts.length} contato(s) importado(s)`);
  };

  const previewContacts = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const { data } = await api.post("/mass-campaigns/contacts-preview", {
        contacts: (Array.isArray(draft.contacts) ? draft.contacts : []).filter((contact) => contactStatus(contact).label === "Válido"),
        use_crm_contacts: draft.use_crm_contacts,
        tags: draft.tags,
        pipeline_id: draft.pipeline_id || null,
        column_id: draft.column_id || null,
      });
      const source = draft.use_crm_contacts || draft.pipeline_id || draft.column_id || draft.tags?.length
        ? "Contatos do CRM"
        : draft.contact_source;
      patchDraft({ contacts: (data.contacts || []).map((item) => ({ ...item, source })), contact_source: source || "Contatos selecionados" });
      toast.success(`${data.count || 0} contato(s) selecionado(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao buscar contatos");
    } finally {
      setBusy(false);
    }
  };

  const buildPayload = (status = "draft") => ({
    name: draft.name,
    channel_id: draft.channel_id,
    status,
    message: draft.message,
    template_name: draft.template_name,
    template_language: draft.template_language,
    template_variables: draft.template_variables,
    attachments: draft.attachments,
    scheduled_at: draft.scheduled_at || null,
    contacts: (Array.isArray(draft.contacts) ? draft.contacts : []).filter((contact) => contactStatus(contact).label === "Válido"),
  });

  const ensureValid = () => {
    const errors = getDraftErrors(draft, selectedChannel);
    if (errors.length) {
      toast.error(errors[0]);
      return false;
    }
    return true;
  };

  const saveCampaign = async (status = "draft") => {
    if (!ensureValid()) return;
    if (status === "scheduled") {
      const scheduleErrors = getScheduleErrors(draft);
      if (scheduleErrors.length) {
        toast.error(scheduleErrors[0]);
        return;
      }
    }
    setBusy(true);
    try {
      await api.post("/mass-campaigns", buildPayload(status));
      toast.success(status === "scheduled" ? "Campanha agendada" : "Campanha salva");
      setDraft(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar campanha");
    } finally {
      setBusy(false);
    }
  };

  const requestConfirm = () => {
    if (!ensureValid()) return;
    setConfirmOpen(true);
  };

  const confirmReadyCampaign = async () => {
    if (!ensureValid()) return;
    setBusy(true);
    try {
      await api.post("/mass-campaigns", buildPayload("ready_to_send"));
      toast.success("Campanha salva como pronta para envio.");
      setDraft(null);
      setConfirmOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar campanha");
    } finally {
      setBusy(false);
    }
  };

  const duplicateCampaign = async (item) => {
    await api.post(`/mass-campaigns/${item.id}/duplicate`);
    toast.success("Campanha duplicada");
    await load();
  };

  const deleteCampaign = async (item) => {
    if (!window.confirm(`Excluir a campanha ${item.name}?`)) return;
    await api.delete(`/mass-campaigns/${item.id}`);
    toast.success("Campanha excluída");
    await load();
  };

  const confirmQueueCampaign = async () => {
    if (!queueCampaign) return;
    setBusy(true);
    try {
      await api.post(`/mass-campaigns/${queueCampaign.id}/queue`);
      toast.success("Envio da campanha concluído")
      setQueueCampaign(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao colocar campanha na fila");
    } finally {
      setBusy(false);
    }
  };

  const openReport = async (item) => {
    const { data } = await api.get(`/mass-campaigns/${item.id}/report`);
    setReport(data);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <div>
          <h2 className="text-xl font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>Envio em Massa</h2>
          <p className="mt-1 text-sm text-slate-400">Campanhas de WhatsApp com detecção automática entre Meta Cloud API e Evolution API.</p>
        </div>
        <Button onClick={openNew} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]"><Plus className="mr-1.5 h-4 w-4" /> Nova Campanha</Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50">
        <div className="grid min-w-[980px] grid-cols-[1.4fr_1.2fr_110px_120px_110px_150px_230px] gap-3 border-b border-slate-800 bg-slate-950/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span>Nome da campanha</span><span>Canal utilizado</span><span>Tipo</span><span>Contatos</span><span>Status</span><span>Data</span><span>Ações</span>
        </div>
        <div className="overflow-x-auto divide-y divide-slate-800">
          {campaigns.length === 0 ? <div className="px-4 py-10 text-center text-sm text-slate-500">Nenhuma campanha criada ainda.</div> : campaigns.map((item) => (
            <div key={item.id} className="grid min-w-[980px] grid-cols-[1.4fr_1.2fr_110px_120px_110px_150px_230px] items-center gap-3 px-4 py-3 text-sm">
              <div className="font-semibold text-slate-100">{item.name}</div>
              <div className="truncate text-slate-300">{item.channel_name || item.channel_id}</div>
              <div className="text-slate-300">{item.type === "META" ? "Meta" : "Evolution"}</div>
              <div className="text-slate-300">{item.contacts_count || 0}</div>
              <div>{statusBadge(item.status)}</div>
              <div className="text-xs text-slate-500">{item.status === "scheduled" && item.scheduled_at ? <>Agendada: {formatCampaignDate(item.scheduled_at)}</> : formatCampaignDate(item.created_at)}</div>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" onClick={() => setDraft({ ...EMPTY_DRAFT, ...item, step: 1, contacts: [] })}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" onClick={() => duplicateCampaign(item)}><Copy className="h-4 w-4" /></Button>
                {["ready_to_send", "queued"].includes(item.status) && <Button size="sm" variant="ghost" title="Enviar agora" className="text-cyan-300 hover:bg-slate-800" onClick={() => setQueueCampaign(item)}><Play className="h-4 w-4" /><span className="sr-only">Enviar agora</span></Button>}
                <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" onClick={() => openReport(item)}><BarChart3 className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-slate-800" onClick={() => deleteCampaign(item)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {draft && <CampaignModal draft={draft} patchDraft={patchDraft} channels={channels} selectedChannel={selectedChannel} isMeta={isMeta} isEvolution={isEvolution} templates={approvedTemplates} filters={filters} busy={busy} isSuperAdmin={isSuperAdmin} onClose={() => { setDraft(null); setConfirmOpen(false); }} onFile={importFile} onPreviewContacts={previewContacts} onSave={saveCampaign} onStart={requestConfirm} />}
      {confirmOpen && draft && <ConfirmModal draft={draft} selectedChannel={selectedChannel} busy={busy} onClose={() => setConfirmOpen(false)} onConfirm={confirmReadyCampaign} />}
      {queueCampaign && <QueueConfirmModal campaign={queueCampaign} busy={busy} onClose={() => setQueueCampaign(null)} onConfirm={confirmQueueCampaign} />}
      {report && <ReportDrawer report={report} onClose={() => setReport(null)} />}
    </div>
  );
}

function CampaignModal({ draft, patchDraft, channels, selectedChannel, isMeta, isEvolution, templates, filters, busy, isSuperAdmin, onClose, onFile, onPreviewContacts, onSave, onStart }) {
  const step = draft.step || 1;
  const safeContacts = Array.isArray(draft.contacts) ? draft.contacts : [];
  const stats = contactStats(safeContacts);
  const selectedTemplate = templates.find((item) => item.name === draft.template_name);
  const canContinue = canContinueStep(step, draft, selectedChannel);
  const finalErrors = getDraftErrors(draft, selectedChannel);
  const canStart = finalErrors.length === 0;

  const goNext = () => {
    if (!canContinue) {
      toast.error(stepHint(step, draft, selectedChannel));
      return;
    }
    patchDraft({ step: Math.min(4, step + 1) });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-4 py-6" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
          <div>
            <h3 className="flex items-center gap-1.5 text-lg font-semibold text-slate-100">
              Nova Campanha
              <HelpTooltip text="4 passos: 1) escolha o canal de envio, 2) dê um nome pra campanha, 3) importe ou busque os contatos, 4) revise e escolha entre salvar rascunho, agendar ou iniciar agora." />
            </h3>
            <p className="text-sm text-slate-500">Etapa {step} de 4</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-900 hover:text-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <WizardStepBoundary resetKey={`${step}-${draft.channel_id}`}>
            {step === 1 && <ChannelStep channels={channels} draft={draft} patchDraft={patchDraft} isSuperAdmin={isSuperAdmin} />}
            {step === 2 && <ConfigStep draft={draft} patchDraft={patchDraft} selectedChannel={selectedChannel} isSuperAdmin={isSuperAdmin} />}
            {step === 3 && <ContactsStep draft={draft} patchDraft={patchDraft} filters={filters} busy={busy} onFile={onFile} onPreviewContacts={onPreviewContacts} stats={stats} />}
            {step === 4 && <ReviewStep draft={draft} patchDraft={patchDraft} selectedChannel={selectedChannel} isMeta={isMeta} isEvolution={isEvolution} templates={templates} selectedTemplate={selectedTemplate} stats={stats} errors={finalErrors} isSuperAdmin={isSuperAdmin} />}
          </WizardStepBoundary>
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t border-slate-800 px-6 py-4">
          <Button variant="outline" className="border-slate-700 bg-slate-900 text-slate-100" disabled={step === 1} onClick={() => patchDraft({ step: Math.max(1, step - 1) })}>Voltar</Button>
          <div className="flex flex-wrap gap-2">
            {step < 4 ? (
              <Button className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52] disabled:cursor-not-allowed disabled:opacity-50" disabled={!canContinue} onClick={goNext}>Continuar</Button>
            ) : (
              <>
                <Button variant="outline" className="border-slate-700 bg-slate-900 text-slate-100" disabled={!canStart || busy} onClick={() => onSave("draft")}>Salvar rascunho</Button>
                <Button
                  title={!draft.scheduled_at ? "Escolha uma data e hora de agendamento na revisão da campanha." : undefined}
                  variant="outline"
                  className="border-blue-500/30 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!canStart || !draft.scheduled_at || busy}
                  onClick={() => onSave("scheduled")}
                >
                  <CalendarClock className="mr-1.5 h-4 w-4" /> Agendar campanha
                </Button>
                <HelpTooltip text="Define uma data e hora futura (campo na etapa de revisão) pra disparar sozinha, sem precisar estar com o CRM aberto. 'Iniciar campanha' dispara na hora; 'Salvar rascunho' só guarda pra editar depois." />
                <Button className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52] disabled:cursor-not-allowed disabled:opacity-50" disabled={!canStart || busy} onClick={onStart}><Play className="mr-1.5 h-4 w-4" /> Iniciar campanha</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

class WizardStepBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[MassCampaignWizard] erro na etapa", error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">Não foi possível carregar esta etapa. Tente voltar e avançar novamente.</div>;
    }
    return this.props.children;
  }
}
function ChannelStep({ channels, draft, patchDraft, isSuperAdmin }) {
  return <section className="space-y-4">
    <div>
      <h4 className="flex items-center gap-1.5 font-semibold text-slate-100">
        Selecionar Canal
        {isSuperAdmin && (
          <HelpTooltip text="Oficial (Meta): exige um template pré-aprovado pra iniciar a conversa com quem nunca falou com você. Não oficial (Evolution/WhatsApp Web): manda mensagem livre direto, sem template, mas com mais risco de bloqueio em disparos muito grandes." />
        )}
      </h4>
      <p className="mt-1 text-sm text-slate-500">
        {isSuperAdmin
          ? "Escolha o número de WhatsApp. O sistema identifica automaticamente se o envio será oficial Meta ou não oficial Evolution."
          : "Escolha o número de WhatsApp que enviará esta campanha."}
      </p>
    </div>
    {channels.length === 0 ? (
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/50 p-6 text-center text-sm text-slate-400">
        <AlertCircle className="mx-auto mb-3 h-6 w-6 text-amber-400" />
        Nenhum canal WhatsApp conectado. Conecte um canal em Configurações &gt; Canais antes de criar uma campanha.
      </div>
    ) : (
      <div className="grid gap-3 md:grid-cols-2">
        {channels.map((channel) => {
          const selected = draft.channel_id === channel.id;
          const connected = channel.status === "connected" && channel.canSend;
          return <button key={channel.id} disabled={!connected} onClick={() => connected && patchDraft({ channel_id: channel.id })} className={`rounded-xl border p-4 text-left transition ${selected ? "border-[#25D366] bg-[#25D366]/10" : "border-slate-800 bg-slate-900/60 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"}`}>
            <div className="flex items-start justify-between gap-3">
              <div><div className="font-semibold text-slate-100">{channel.displayName}</div><div className="mt-1 text-xs text-slate-400">Número: {channel.phone || "Não informado"}</div></div>
              {isSuperAdmin && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${channel.type === "META" ? "bg-blue-500/15 text-blue-300" : "bg-amber-500/15 text-amber-300"}`}>{channel.type === "META" ? "Oficial" : "Não oficial"}</span>
              )}
            </div>
            <div className="mt-3 grid gap-1 text-xs text-slate-400">
              {isSuperAdmin && <span>Tipo: {channelProviderLabel(channel)}</span>}
              <span>Status: <b className={connected ? "text-[#25D366]" : "text-amber-300"}>{statusLabel(channel.status)}</b></span>
            </div>
            {selected && <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#25D366]"><CheckCircle2 className="h-3.5 w-3.5" /> Canal selecionado</div>}
            {!connected && <div className="mt-3 text-xs text-amber-300">Canal indisponível para envio no momento.</div>}
          </button>;
        })}
      </div>
    )}
  </section>;
}

function ConfigStep({ draft, patchDraft, selectedChannel, isSuperAdmin }) {
  return <section className="space-y-4"><h4 className="font-semibold text-slate-100">Configuração da campanha</h4><div><Label className="text-xs uppercase text-slate-500">Nome da campanha</Label><Input value={draft.name} onChange={(e) => patchDraft({ name: e.target.value })} placeholder="Promoção Junho" className="mt-1 border-slate-700 bg-slate-900 text-slate-100" /></div>{selectedChannel && <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300"><div>Canal: <b>{selectedChannel.displayName}</b></div>{isSuperAdmin && <div className="mt-1">Tipo da campanha: <b>{channelTypeLabel(selectedChannel)}</b></div>}<div className="mt-1 text-xs text-slate-500">{selectedChannel.type === "META" ? "Este canal exige um template aprovado para o primeiro contato." : "Este canal permite o envio de mensagem livre."}</div></div>}</section>;
}

function ContactsStep({ draft, patchDraft, filters, busy, onFile, onPreviewContacts, stats }) {
  const safePipelines = Array.isArray(filters?.pipelines) ? filters.pipelines : [];
  const safeColumns = Array.isArray(filters?.columns) ? filters.columns : [];
  const safeTags = Array.isArray(draft.tags) ? draft.tags : [];
  const safeContacts = Array.isArray(draft.contacts) ? draft.contacts : [];
  return <section className="space-y-5">
    <div><h4 className="font-semibold text-slate-100">Importar ou selecionar contatos</h4><p className="mt-1 text-sm text-slate-500">Adicione os contatos que receberão a campanha. Você pode importar uma planilha ou buscar contatos do CRM.</p></div>
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/50 p-5 text-sm text-slate-400">
        <FileSpreadsheet className="mb-2 h-6 w-6 text-[#25D366]" />
        <p>Envie um arquivo CSV ou XLSX com pelo menos a coluna telefone. Recomendamos usar o modelo para evitar erros na importação.</p>
        <p className="mt-2 text-xs text-slate-500">Formato recomendado para telefone: 5516999999999</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center rounded-lg bg-[#25D366] px-3 py-2 text-xs font-semibold text-slate-950 hover:bg-[#1EAC52]">Enviar planilha<input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} /></label>
          <Button type="button" variant="outline" onClick={downloadTemplateCsv} className="border-slate-700 bg-slate-950 text-xs text-slate-100"><Download className="mr-1.5 h-3.5 w-3.5" /> Baixar planilha modelo</Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={draft.use_crm_contacts} onChange={(e) => patchDraft({ use_crm_contacts: e.target.checked })} /> Selecionar contatos do CRM</label><div className="mt-3 grid gap-2"><select value={draft.pipeline_id} onChange={(e) => patchDraft({ pipeline_id: e.target.value })} className="h-10 rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"><option value="">Todos os funis</option>{safePipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select><select value={draft.column_id} onChange={(e) => patchDraft({ column_id: e.target.value })} className="h-10 rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"><option value="">Todos os estágios</option>{safeColumns.filter((c) => !draft.pipeline_id || c.pipeline_id === draft.pipeline_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select><Input value={safeTags.join(", ")} onChange={(e) => patchDraft({ tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Tags separadas por vírgula" className="border-slate-700 bg-slate-950 text-slate-100" /></div><Button className="mt-3 w-full bg-slate-800 text-slate-100 hover:bg-slate-700" onClick={onPreviewContacts} disabled={busy}><Search className="mr-1.5 h-4 w-4" /> Buscar contatos</Button></div>
    </div>
    <SummaryStats stats={stats} />
    <ContactsPreview contacts={safeContacts} source={draft.contact_source} />
  </section>;
}
function ReviewStep({ draft, patchDraft, selectedChannel, isMeta, isEvolution, templates, selectedTemplate, stats, errors, isSuperAdmin }) {
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const safeContacts = Array.isArray(draft.contacts) ? draft.contacts : [];
  const firstValidContact = safeContacts.find((contact) => contactStatus(contact).label === "Válido");
  const messagePreview = isMeta ? (selectedTemplate?.body || "Template não selecionado") : (draft.message || "Mensagem não informada");
  const variablePreview = firstValidContact ? renderMessageWithContactVariables(messagePreview, firstValidContact) : "A prévia será exibida após selecionar contatos válidos.";
  const attachment = draft.attachments?.[0] || null;

  const handleAttachmentChange = async (file) => {
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url || data?.mediaUrl;
      if (!url) throw new Error("Upload sem URL de retorno");
      patchDraft({
        attachments: [{
          name: file.name,
          size: file.size,
          type: file.type,
          url,
          media_type: data?.media_type || data?.mediaType,
          content_type: data?.content_type || file.type,
        }],
      });
      toast.success(`${file.name} anexado`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao enviar anexo");
    } finally {
      setUploadingAttachment(false);
    }
  };
  const removeAttachment = () => patchDraft({ attachments: [] });

  const scheduleValue = draft.scheduled_at_local ?? (draft.scheduled_at ? toLocalDatetimeInputValue(new Date(draft.scheduled_at)) : "");
  const minScheduleValue = toLocalDatetimeInputValue(new Date(Date.now() + 60000));
  const handleScheduleChange = (localValue) => {
    if (!localValue) {
      patchDraft({ scheduled_at_local: "", scheduled_at: "" });
      return;
    }
    const asDate = new Date(localValue);
    patchDraft({
      scheduled_at_local: localValue,
      scheduled_at: Number.isNaN(asDate.getTime()) ? "" : asDate.toISOString(),
    });
  };

  return <section className="space-y-5">
    <div><h4 className="font-semibold text-slate-100">Mensagem e revisão</h4><p className="mt-1 text-sm text-slate-500">Revise a mensagem e os dados da campanha antes de salvar.</p></div>
    <div className="space-y-2 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <Label className="text-xs uppercase text-slate-500">Agendamento (opcional)</Label>
      <p className="text-xs text-slate-500">Escolha uma data e hora futura para disparar a campanha automaticamente com "Agendar campanha", ou deixe em branco para enviar manualmente com "Iniciar campanha".</p>
      <Input
        type="datetime-local"
        min={minScheduleValue}
        value={scheduleValue}
        onChange={(e) => handleScheduleChange(e.target.value)}
        className="border-slate-700 bg-slate-900 text-slate-100"
      />
    </div>
    {isMeta && <div className="space-y-3"><Label className="text-xs uppercase text-slate-500">{isSuperAdmin ? "Template Oficial" : "Modelo de mensagem"}</Label><select value={draft.template_name} onChange={(e) => { const t = templates.find((item) => item.name === e.target.value); patchDraft({ template_name: e.target.value, template_language: t?.language || "pt_BR" }); }} className="h-10 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100"><option value="">Selecione um template aprovado</option>{templates.map((template) => <option key={template.id || template.name} value={template.name}>{template.name}</option>)}</select><div className="rounded-xl border border-blue-500/20 bg-blue-500/10 p-4 text-sm text-blue-100">Este canal exige template aprovado. Não é permitido editar texto livre.</div><div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">{selectedTemplate?.body || "Nenhum template aprovado selecionado."}</div><Input value={draft.template_variables?.["1"] || ""} onChange={(e) => patchDraft({ template_variables: { ...draft.template_variables, "1": e.target.value } })} placeholder="{{1}} = {{nome}}" className="border-slate-700 bg-slate-900 text-slate-100" /></div>}
    {isEvolution && <div className="space-y-3"><div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">Recomendamos intervalos seguros para reduzir risco de bloqueio do número. Mensagem livre está habilitada.</div><Label className="text-xs uppercase text-slate-500">Editor de Mensagem</Label><textarea value={draft.message} onChange={(e) => patchDraft({ message: e.target.value })} placeholder="Olá {{nome}}, tudo bem?" className="min-h-[160px] w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-slate-100 outline-none" /><div className="text-xs text-slate-500">Variáveis: {"{{nome}}"}, {"{{empresa}}"}, {"{{email}}"}, {"{{telefone}}"}</div><label className="block rounded-xl border border-dashed border-slate-700 bg-slate-900/50 p-4 text-sm text-slate-400">Anexo: imagem, vídeo, documento ou PDF (1 arquivo por campanha)<input type="file" disabled={uploadingAttachment} className="mt-2 block text-xs disabled:opacity-50" onChange={(e) => handleAttachmentChange(e.target.files?.[0])} />{uploadingAttachment && <span className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Enviando anexo...</span>}{!uploadingAttachment && attachment && <div className="mt-2 flex items-center gap-2 text-xs text-[#25D366]"><CheckCircle2 className="h-3.5 w-3.5" /> {attachment.name}<button type="button" onClick={removeAttachment} className="text-slate-400 underline hover:text-red-400">remover</button></div>}</label></div>}
    <div className="grid gap-3 md:grid-cols-2"><ReviewCard label="Nome da campanha" value={draft.name || "Nome não informado"} />{isSuperAdmin && <ReviewCard label="Tipo" value={selectedChannel ? channelTypeLabel(selectedChannel) : "Tipo não definido"} />}<ReviewCard label="Canal selecionado" value={selectedChannel?.displayName || "Canal não selecionado"} /><ReviewCard label="Número do canal" value={selectedChannel?.phone ? formatPhone(selectedChannel.phone) : "Não informado"} /></div>
    <SummaryStats stats={stats} />
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Prévia da mensagem</p><div className="whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950 p-3 text-sm text-slate-200">{variablePreview}</div><div className="mt-3 text-xs text-slate-500">Anexo: {attachment ? attachment.name : "sem anexo"}</div></div>
    <ContactsPreview contacts={safeContacts} source={draft.contact_source} />
    {errors.length > 0 && <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200"><AlertCircle className="mr-2 inline h-4 w-4" />{errors[0]}</div>}
  </section>;
}

function ReviewCard({ label, value, tone = "slate" }) {
  const color = tone === "green" ? "text-[#25D366]" : tone === "amber" ? "text-amber-300" : tone === "red" ? "text-red-300" : "text-slate-100";
  return <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-1 text-sm font-semibold ${color}`}>{value}</p></div>;
}

function SummaryStats({ stats }) {
  const safeStats = stats || { total: 0, valid: 0, warning: 0, invalid: 0 };
  return <div className="grid gap-3 md:grid-cols-4"><ReviewCard label="Total de contatos" value={safeStats.total} /><ReviewCard label="Válidos" value={safeStats.valid} tone="green" /><ReviewCard label="Com alerta" value={safeStats.warning} tone="amber" /><ReviewCard label="Inválidos" value={safeStats.invalid} tone="red" /></div>;
}

function ContactsPreview({ contacts = [], source }) {
  const [expanded, setExpanded] = useState(false);
  const safeContacts = Array.isArray(contacts) ? contacts : [];
  const rows = expanded ? safeContacts : safeContacts.slice(0, 10);
  if (!safeContacts.length) return <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-500">Nenhum contato selecionado ainda. Importe uma planilha ou busque contatos do CRM.</div>;
  return <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60"><div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3"><div><h5 className="text-sm font-semibold text-slate-100">Contatos selecionados</h5><p className="text-xs text-slate-500">Exibindo {rows.length} de {safeContacts.length} contatos</p></div>{safeContacts.length > 10 && <Button type="button" variant="outline" onClick={() => setExpanded((v) => !v)} className="border-slate-700 bg-slate-950 text-xs text-slate-100"><Eye className="mr-1.5 h-3.5 w-3.5" /> {expanded ? "Ver menos" : "Ver todos"}</Button>}</div><div className="overflow-x-auto"><table className="min-w-[720px] w-full text-left text-sm"><thead className="bg-slate-950/70 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Nome</th><th className="px-4 py-2">Telefone</th><th className="px-4 py-2">Origem</th><th className="px-4 py-2">Status</th></tr></thead><tbody className="divide-y divide-slate-800">{rows.map((contact, index) => { const status = contactStatus(contact); const tone = status.tone === "green" ? "text-[#25D366]" : status.tone === "amber" ? "text-amber-300" : "text-red-300"; return <tr key={`${contact.phone || contact.email || index}-${index}`}><td className="px-4 py-2 text-slate-200">{contact.name || "Sem nome"}</td><td className="px-4 py-2 text-slate-300">{formatPhone(contact.phone)}</td><td className="px-4 py-2 text-slate-400">{contact.source || source || "Selecionado"}</td><td className={`px-4 py-2 ${tone}`}><span className="font-semibold">{status.label}</span><span className="ml-2 text-xs text-slate-500">{status.reason}</span></td></tr>; })}</tbody></table></div></div>;
}

function ConfirmModal({ draft, selectedChannel, busy, onClose, onConfirm }) {
  const stats = contactStats(draft.contacts || []);
  return <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 px-4" onClick={onClose}><div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}><h3 className="text-lg font-semibold text-slate-100">Confirmar criação da campanha</h3><p className="mt-2 text-sm leading-relaxed text-slate-400">Revise os dados antes de continuar. Nesta etapa o Cliver CRM ainda não fará disparos reais; a campanha será salva como pronta para envio.</p><div className="mt-4 space-y-2 rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300"><div>Campanha: <b className="text-slate-100">{draft.name}</b></div><div>Canal: <b className="text-slate-100">{selectedChannel?.displayName}</b></div><div>Contatos válidos: <b className="text-slate-100">{stats.valid}</b></div><div>Status após confirmar: <b className="text-cyan-300">Pronta para envio</b></div></div><div className="mt-5 flex justify-end gap-2"><Button variant="outline" className="border-slate-700 bg-slate-900 text-slate-100" onClick={onClose}>Cancelar</Button><Button className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]" disabled={busy} onClick={onConfirm}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Confirmar e salvar campanha</Button></div></div></div>;
}
function QueueConfirmModal({ campaign, busy, onClose, onConfirm }) {
  return <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 px-4" onClick={onClose}><div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}><h3 className="text-lg font-semibold text-slate-100">Confirmar envio da campanha</h3><p className="mt-2 text-sm leading-relaxed text-slate-400">Esta ação iniciará o envio da campanha agora. As mensagens serão personalizadas por contato e registradas individualmente.</p><div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300"><div>Campanha: <b className="text-slate-100">{campaign.name}</b></div><div className="mt-1">Status após confirmar: <b className="text-amber-300">Enviando</b></div></div><div className="mt-5 flex justify-end gap-2"><Button variant="outline" className="border-slate-700 bg-slate-900 text-slate-100" onClick={onClose}>Cancelar</Button><Button className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]" disabled={busy} onClick={onConfirm}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Confirmar envio</Button></div></div></div>;
}

function ReportDrawer({ report, onClose }) {
  const items = [["Total", report.total], ["Enviando", report.sending], ["Enviados", report.sent], ["Entregues", report.delivered], ["Lidos", report.read], ["Respondidos", report.responded], ["Falhas", report.failed]];
  const total = Math.max(Number(report.total || 0), 1);
  const status = report.campaign?.status;
  return <div className="fixed inset-0 z-[80] flex justify-end bg-black/60" onClick={onClose}><aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-800 bg-slate-950 p-6" onClick={(e) => e.stopPropagation()}><div className="mb-6 flex items-start justify-between"><div><h3 className="text-lg font-semibold text-slate-100">Relatório da campanha</h3><p className="text-sm text-slate-500">{report.campaign?.name}</p></div><button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-900 hover:text-slate-100"><X className="h-5 w-5" /></button></div><div className="mb-4 flex items-center gap-2 text-sm text-slate-300"><span>Status atual:</span>{statusBadge(status)}</div>{status === "ready_to_send" && <div className="mb-4 rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-4 text-sm text-cyan-100">Campanha pronta, mas ainda não enviada.</div>}{status === "queued" && <div className="mb-4 rounded-xl border border-sky-500/20 bg-sky-500/10 p-4 text-sm text-sky-100">Campanha na fila de envio.</div>}{status === "running" && <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">Campanha em envio neste momento.</div>}<div className="grid gap-3 sm:grid-cols-2">{items.map(([label, value]) => <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 text-2xl font-bold text-slate-100">{value || 0}</p><div className="mt-3 h-1.5 rounded-full bg-slate-800"><div className="h-full rounded-full bg-[#25D366]" style={{ width: `${Math.min(100, ((value || 0) / total) * 100)}%` }} /></div></div>)}</div></aside></div>;
}